import React, { useState, useEffect } from 'react';

function PropertiesPanel({ selectedComponent, onUpdateComponent }) {
  const [localProps, setLocalProps] = useState({});
  const [localStyle, setLocalStyle] = useState({});

  useEffect(() => {
    if (selectedComponent) {
      setLocalProps(selectedComponent.props || {});
      setLocalStyle(selectedComponent.style || {});
    }
  }, [selectedComponent]);

  const updateProps = (key, value) => {
    const newProps = { ...localProps, [key]: value };
    setLocalProps(newProps);
    if (onUpdateComponent) {
      onUpdateComponent(selectedComponent.id, { props: newProps });
    }
  };

  const updateStyle = (key, value) => {
    const newStyle = { ...localStyle, [key]: value };
    setLocalStyle(newStyle);
    if (onUpdateComponent) {
      onUpdateComponent(selectedComponent.id, { style: newStyle });
    }
  };

  if (!selectedComponent) {
    return (
      <div className="properties-panel">
        <div className="panel-header">
          <h3>
            <i className="fas fa-cog me-2"></i>
            Properties
          </h3>
        </div>
        <div className="panel-content">
          <div className="no-selection">
            <i className="fas fa-mouse-pointer fa-2x text-muted mb-3"></i>
            <p className="text-muted">Select a component to edit its properties</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="properties-panel">
      <div className="panel-header">
        <h3>
          <i className="fas fa-cog me-2"></i>
          Properties
        </h3>
        <div className="component-type-badge">
          {selectedComponent.type}
        </div>
      </div>

      <div className="panel-content">
        <div className="property-section">
          <h5>Content</h5>
          {renderContentProperties(selectedComponent.type, localProps, updateProps)}
        </div>

        <div className="property-section">
          <h5>Styling</h5>
          {renderStyleProperties(selectedComponent.type, localStyle, updateStyle)}
        </div>

        <div className="property-section">
          <h5>Layout</h5>
          <div className="property-group">
            <label>Margin</label>
            <div className="spacing-controls">
              <input
                type="text"
                placeholder="Top"
                value={localStyle.marginTop || ''}
                onChange={(e) => updateStyle('marginTop', e.target.value)}
                className="form-control spacing-input"
              />
              <input
                type="text"
                placeholder="Right"
                value={localStyle.marginRight || ''}
                onChange={(e) => updateStyle('marginRight', e.target.value)}
                className="form-control spacing-input"
              />
              <input
                type="text"
                placeholder="Bottom"
                value={localStyle.marginBottom || ''}
                onChange={(e) => updateStyle('marginBottom', e.target.value)}
                className="form-control spacing-input"
              />
              <input
                type="text"
                placeholder="Left"
                value={localStyle.marginLeft || ''}
                onChange={(e) => updateStyle('marginLeft', e.target.value)}
                className="form-control spacing-input"
              />
            </div>
          </div>

          <div className="property-group">
            <label>Padding</label>
            <div className="spacing-controls">
              <input
                type="text"
                placeholder="Top"
                value={localStyle.paddingTop || ''}
                onChange={(e) => updateStyle('paddingTop', e.target.value)}
                className="form-control spacing-input"
              />
              <input
                type="text"
                placeholder="Right"
                value={localStyle.paddingRight || ''}
                onChange={(e) => updateStyle('paddingRight', e.target.value)}
                className="form-control spacing-input"
              />
              <input
                type="text"
                placeholder="Bottom"
                value={localStyle.paddingBottom || ''}
                onChange={(e) => updateStyle('paddingBottom', e.target.value)}
                className="form-control spacing-input"
              />
              <input
                type="text"
                placeholder="Left"
                value={localStyle.paddingLeft || ''}
                onChange={(e) => updateStyle('paddingLeft', e.target.value)}
                className="form-control spacing-input"
              />
            </div>
          </div>
        </div>

        <div className="property-section">
          <h5>Advanced</h5>
          <div className="property-group">
            <label>Custom CSS Class</label>
            <input
              type="text"
              value={localProps.className || ''}
              onChange={(e) => updateProps('className', e.target.value)}
              placeholder="custom-class"
              className="form-control"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function renderContentProperties(componentType, props, updateProps) {
  switch (componentType) {
    case 'text':
      return (
        <>
          <div className="property-group">
            <label>Text Content</label>
            <textarea
              value={props.content || ''}
              onChange={(e) => updateProps('content', e.target.value)}
              placeholder="Enter your text..."
              className="form-control"
              rows={3}
            />
          </div>
          <div className="property-group">
            <label>HTML Tag</label>
            <select
              value={props.tag || 'p'}
              onChange={(e) => updateProps('tag', e.target.value)}
              className="form-control"
            >
              <option value="p">Paragraph</option>
              <option value="h1">Heading 1</option>
              <option value="h2">Heading 2</option>
              <option value="h3">Heading 3</option>
              <option value="h4">Heading 4</option>
              <option value="h5">Heading 5</option>
              <option value="h6">Heading 6</option>
              <option value="span">Span</option>
            </select>
          </div>
        </>
      );

    case 'image':
      return (
        <>
          <div className="property-group">
            <label>Image URL</label>
            <input
              type="text"
              value={props.src || ''}
              onChange={(e) => updateProps('src', e.target.value)}
              placeholder="https://example.com/image.jpg"
              className="form-control"
            />
          </div>
          <div className="property-group">
            <label>Alt Text</label>
            <input
              type="text"
              value={props.alt || ''}
              onChange={(e) => updateProps('alt', e.target.value)}
              placeholder="Image description"
              className="form-control"
            />
          </div>
        </>
      );

    case 'button':
      return (
        <>
          <div className="property-group">
            <label>Button Text</label>
            <input
              type="text"
              value={props.text || ''}
              onChange={(e) => updateProps('text', e.target.value)}
              placeholder="Click Me"
              className="form-control"
            />
          </div>
          <div className="property-group">
            <label>Link URL</label>
            <input
              type="text"
              value={props.link || ''}
              onChange={(e) => updateProps('link', e.target.value)}
              placeholder="https://example.com"
              className="form-control"
            />
          </div>
          <div className="property-group">
            <label>Button Style</label>
            <select
              value={props.variant || 'primary'}
              onChange={(e) => updateProps('variant', e.target.value)}
              className="form-control"
            >
              <option value="primary">Primary</option>
              <option value="secondary">Secondary</option>
              <option value="success">Success</option>
              <option value="danger">Danger</option>
              <option value="warning">Warning</option>
              <option value="info">Info</option>
              <option value="outline">Outline</option>
            </select>
          </div>
        </>
      );

    case 'banner':
      return (
        <>
          <div className="property-group">
            <label>Title</label>
            <input
              type="text"
              value={props.title || ''}
              onChange={(e) => updateProps('title', e.target.value)}
              placeholder="Welcome"
              className="form-control"
            />
          </div>
          <div className="property-group">
            <label>Subtitle</label>
            <input
              type="text"
              value={props.subtitle || ''}
              onChange={(e) => updateProps('subtitle', e.target.value)}
              placeholder="Experience great food"
              className="form-control"
            />
          </div>
          <div className="property-group">
            <label>Background Image</label>
            <input
              type="text"
              value={props.backgroundImage || ''}
              onChange={(e) => updateProps('backgroundImage', e.target.value)}
              placeholder="https://example.com/bg.jpg"
              className="form-control"
            />
          </div>
        </>
      );

    default:
      return (
        <div className="text-muted">
          <i className="fas fa-info-circle me-2"></i>
          Properties for {componentType} component
        </div>
      );
  }
}

function renderStyleProperties(componentType, style, updateStyle) {
  return (
    <>
      <div className="property-group">
        <label>Background Color</label>
        <div className="color-input-group">
          <input
            type="color"
            value={style.backgroundColor || '#ffffff'}
            onChange={(e) => updateStyle('backgroundColor', e.target.value)}
            className="color-picker"
          />
          <input
            type="text"
            value={style.backgroundColor || ''}
            onChange={(e) => updateStyle('backgroundColor', e.target.value)}
            placeholder="#ffffff"
            className="form-control color-text"
          />
        </div>
      </div>

      <div className="property-group">
        <label>Text Color</label>
        <div className="color-input-group">
          <input
            type="color"
            value={style.color || '#333333'}
            onChange={(e) => updateStyle('color', e.target.value)}
            className="color-picker"
          />
          <input
            type="text"
            value={style.color || ''}
            onChange={(e) => updateStyle('color', e.target.value)}
            placeholder="#333333"
            className="form-control color-text"
          />
        </div>
      </div>

      {(componentType === 'text' || componentType === 'button') && (
        <div className="property-group">
          <label>Font Size</label>
          <input
            type="text"
            value={style.fontSize || ''}
            onChange={(e) => updateStyle('fontSize', e.target.value)}
            placeholder="16px"
            className="form-control"
          />
        </div>
      )}

      <div className="property-group">
        <label>Border Radius</label>
        <input
          type="text"
          value={style.borderRadius || ''}
          onChange={(e) => updateStyle('borderRadius', e.target.value)}
          placeholder="0px"
          className="form-control"
        />
      </div>
    </>
  );
}

export default PropertiesPanel;